import api, { getErrorMessage } from './api';

const usersService = {
  /**
   * Get all users (admin only, paginated)
   * @param {number} skip - Number of records to skip
   * @param {number} limit - Number of records to return
   * @returns {Promise<object>} - Paginated users list
   */
  getUsers: async (skip = 0, limit = 100) => {
    try {
      const response = await api.get('/api/v1/users/', {
        params: { skip, limit },
      });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get user by ID (admin only)
   * @param {string} userId - User ID
   * @returns {Promise<object>} - User data
   */
  getUserById: async (userId) => {
    try {
      const response = await api.get(`/api/v1/users/${userId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get current user's profile
   * @returns {Promise<object>} - Current user data
   */
  getCurrentUser: async () => {
    try {
      const response = await api.get('/api/v1/users/me');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Update current user's profile
   * @param {object} userData - User data to update
   * @param {string} userData.full_name - Full name
   * @param {string} userData.email - Email
   * @returns {Promise<object>} - Updated user data
   */
  updateCurrentUser: async (userData) => {
    try {
      const response = await api.patch('/api/v1/users/me', userData);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Change current user's password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<object>} - Success message
   */
  changePassword: async (currentPassword, newPassword) => {
    try {
      const response = await api.patch('/api/v1/users/me/password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Delete current user's account
   * @returns {Promise<object>} - Success message
   */
  deleteCurrentUser: async () => {
    try {
      const response = await api.delete('/api/v1/users/me');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Create new user (admin only)
   * @param {object} userData - User data
   * @param {string} userData.email - Email
   * @param {string} userData.password - Password
   * @param {string} userData.full_name - Full name
   * @param {string} userData.role - Role (user, team_admin, admin, super_admin)
   * @param {boolean} userData.is_active - Is active
   * @param {string} userData.organization_id - Organization ID
   * @returns {Promise<object>} - Created user data
   */
  createUser: async (userData) => {
    try {
      const response = await api.post('/api/v1/users/', userData);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Update user (admin only)
   * @param {string} userId - User ID
   * @param {object} userData - User data to update
   * @returns {Promise<object>} - Updated user data
   */
  updateUser: async (userId, userData) => {
    try {
      const response = await api.patch(`/api/v1/users/${userId}`, userData);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Delete user (admin only)
   * @param {string} userId - User ID
   * @returns {Promise<object>} - Success message
   */
  deleteUser: async (userId) => {
    try {
      const response = await api.delete(`/api/v1/users/${userId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get colleagues in the same organization (available to all authenticated users)
   * @param {number} skip - Number of records to skip
   * @param {number} limit - Number of records to return
   * @param {string} search - Optional search term
   * @returns {Promise<object>} - Paginated colleagues list
   */
  getColleagues: async (skip = 0, limit = 100, search = null) => {
    try {
      const params = { skip, limit };
      if (search) params.search = search;
      const response = await api.get('/api/v1/users/colleagues', { params });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },
};

// Aliases for compatibility
usersService.listUsers = usersService.getUsers;
usersService.list = usersService.getUsers;
usersService.listColleagues = usersService.getColleagues;

export default usersService;
